import { getServices, operate } from '@/api/cloud';
import { getDeviceMessage, getDeviceOverview } from '@/api/drive';
import { NormalBreadcrumb } from '@/components/Breadcrumb';
import {
  LeftOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  QuestionCircleOutlined,
  RightOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { Column, Line } from '@ant-design/plots';
import { useRequest } from 'ahooks';
import { Button, Card, Col, Dropdown, MenuProps, message, Progress, Row, Tag, theme, Tooltip } from 'antd';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import MiniAreaChart, { MiniAreaChartDataPoint } from './components/MiniAreaChart';
import './swiper.less';

// 获取负载状态颜色和文本
const getLoadStatus = (load: number) => {
  if (load < 0.7) {
    return { color: '#52c41a', status: '正常', strokeColor: '#52c41a' };
  } else if (load >= 0.7 && load < 1.0) {
    return { color: '#faad14', status: '偏高', strokeColor: '#faad14' };
  } else {
    return { color: '#ff4d4f', status: '过载', strokeColor: '#ff4d4f' };
  }
};

function FirstCard({ deviceId, setDeviceId, setDeviceContainerName }: { deviceId: string; setDeviceId: (id: string) => void; setDeviceContainerName: (name: string) => void }) {
  const { token } = theme.useToken();
  const { data } = useRequest(getDeviceOverview);
  
  // 维护每个驱动的历史负载数据
  const [loadHistoryData, setLoadHistoryData] = useState<Record<string, MiniAreaChartDataPoint[]>>({});
  
  // 维护每个驱动的运行状态（用于立即更新 UI）
  const [driverStatus, setDriverStatus] = useState<Record<string, number>>({});
  
  // 操作状态管理
  const [submitting, setSubmitting] = useState(false);
  
  // 分页状态管理
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 3; // 每页显示3个驱动
  
  // 计算总页数
  const totalDrivers = data?.result?.length || 0;
  const totalPages = Math.ceil(totalDrivers / pageSize);
  
  // 获取当前页的驱动数据
  const getCurrentPageData = () => {
    if (!data?.result) return [];
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return data.result.slice(startIndex, endIndex);
  };
  
  // 翻页函数
  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };
  
  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };
  
  // 启动/停止操作
  const { runAsync } = useRequest(operate, { manual: true });
  const sendOperate = (driverData: { id?: string; run_status: number }) => {
    setSubmitting(true);
    runAsync(driverData)
      .then(() => {
        message.success(driverData.run_status === 1 ? '启动成功' : '停止成功');
        // 立即更新驱动状态
        if (driverData.id) {
          setDriverStatus(prev => ({
            ...prev,
            [driverData.id!]: driverData.run_status,
          }));
        }
      })
      .catch(() => {})
      .finally(() => {
        setSubmitting(false);
      });
  };
  
  // 初始化历史数据
  useEffect(() => {
    if (data?.result && data.result.length > 0) {
      const initialData: Record<string, MiniAreaChartDataPoint[]> = {};
      const now = Date.now();
      
      data.result.forEach((item: any) => {
        // 为每个驱动初始化空数组，等待 API 数据填充
        initialData[item.id] = [];
      });
      
      setLoadHistoryData(initialData);
      console.log(' 图表数据初始化完成，开始从 API 获取实时数据...');
    }
  }, [data?.result]);
  
  //  从 API 获取实时负载数据（每1秒调用一次）
  useEffect(() => {
    if (!data?.result || data.result.length === 0) return;
    
    console.log('  启动 API 轮询，每1秒更新一次');
    
    // 定义获取单个驱动数据的函数
    const fetchDriverData = async (item: any) => {
      try {
        const response = await getServices({
          metric_time: '1s',
          metric_tags: 'container_load',
          container_name: item.containerName,
        });
        
        if (response.success && response.result?.series && response.result.series.length > 0) {
          const seriesData = response.result.series[0];
          const { times, values } = seriesData;
          
          // 将 API 返回的数据转换为图表数据格式
          // 时间格式: "2025-12-17 15:12:00"
          const newDataPoints: MiniAreaChartDataPoint[] = times.map((time, index) => ({
            time: dayjs(time, 'YYYY-MM-DD HH:mm:ss').valueOf(), // 使用 dayjs 解析时间
            value: values[index] * 100, // 转换为百分比 (0-1 → 0-100)
          }));
          
          return { id: item.id, name: item.name, data: newDataPoints };
        }
      } catch (error) {
        console.error(` 获取驱动 ${item.name} 数据失败:`, error);
      }
      return null;
    };
    
    // 首次立即执行
    const updateAllDrivers = async () => {
      const promises = data.result.map((item: any) => fetchDriverData(item));
      const results = await Promise.all(promises);
      
      setLoadHistoryData(prev => {
        const updated = { ...prev };
        
        results.forEach(result => {
          if (result && result.data.length > 0) {
            // 使用 API 返回的所有数据点（通常是最近60个点）
            updated[result.id] = result.data;
            
            // 获取最新的负载值
            const latestValue = result.data[result.data.length - 1]?.value || 0;
            console.log(` ${result.name}: 获取 ${result.data.length} 个数据点，当前负载=${latestValue.toFixed(1)}%`);
          }
        });
        
        return updated;
      });
      
      const successCount = results.filter(r => r !== null).length;
      const totalDataPoints = results.reduce((sum, r) => sum + (r?.data.length || 0), 0);
      console.log(` API 数据更新完成: ${successCount}/${data.result.length} 个驱动，共 ${totalDataPoints} 个数据点`);
    };
    
    // 立即执行一次
    updateAllDrivers();
    
    // 设置定时器，每5秒执行一次
    const timer = setInterval(updateAllDrivers, 5000);
    
    return () => {
      console.log('  停止 API 数据轮询');
      clearInterval(timer);
    };
  }, [data?.result]);
  
  useEffect(() => {
    if (!deviceId && data?.result?.[0]?.id) {
      setDeviceId(data.result[0].id);
      setDeviceContainerName(data.result[0].containerName || '');
    }
  }, [deviceId, data?.result]);

  // 处理驱动操作
  const handleDriverAction = (action: string, item: any) => {
    if (action === 'start') {
      sendOperate({ id: item.id, run_status: 1 });
    } else if (action === 'stop') {
      sendOperate({ id: item.id, run_status: 2 });
    }
  };

  const getDropdownItems = (item: any): MenuProps['items'] => [
    {
      key: 'start',
      label: '启动',
      icon: <PlayCircleOutlined />,
      onClick: () => handleDriverAction('start', item),
    },
    {
      key: 'stop',
      label: '停止',
      icon: <PauseCircleOutlined />,
      onClick: () => handleDriverAction('stop', item),
    },
  ];

  return (
    <Card 
      className="m20" 
      title={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>数据概览</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Button 
              type="text" 
              icon={<LeftOutlined />} 
              onClick={handlePrevPage}
              disabled={currentPage === 1}
              size="small"
            />
            <Button 
              type="text" 
              icon={<RightOutlined />} 
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              size="small"
            />
          </div>
        </div>
      }
    >
      <Row gutter={[20, 20]}>
        {getCurrentPageData().map((item: any) => {
          // 获取当前驱动的运行状态（优先使用本地更新的状态）
          const currentRunStatus = driverStatus[item.id] ?? item.runStatus;
          
          // 获取该驱动的最新负载值（从历史数据中取最新的点）
          const historyData = loadHistoryData[item.id] || [];
          const latestLoadValue = historyData.length > 0 
            ? historyData[historyData.length - 1].value / 100  // 转换回 0-1 范围
            : (item.container_load || 0);
          
          const loadStatus = getLoadStatus(latestLoadValue);
          const loadPercentage = Math.min(latestLoadValue * 100, 100);
          
          return (
             <Col span={8} key={item.id}>
               <Card
                 style={{
                   cursor: 'pointer',
                   borderRadius: '8px',
                   position: 'relative',
                   border: deviceId === item.id ? `2px solid ${token.colorPrimary}` : '1px solid #e8e8e8',
                   backgroundColor: '#fafafa',
                   boxShadow: '0 2px 8px 0 rgba(0, 0, 0, 0.08)',
                 }}
                 onClick={() => {
                   setDeviceId(item.id);
                   setDeviceContainerName(item.containerName);
                 }}
               >
                {/* 设置按钮 */}
                <div style={{ 
                  position: 'absolute', 
                  top: '16px', 
                  right: '16px',
                }}>
                  <Dropdown 
                    menu={{ items: getDropdownItems(item) }} 
                    trigger={['click']}
                    placement="bottomRight"
                  >
                    <Button 
                      type="text" 
                      icon={<SettingOutlined />} 
                      size="small"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </Dropdown>
                </div>

                {/* 驱动名称 */}
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  marginBottom: '8px',
                  paddingRight: '60px',
                }}>
                  <div style={{ 
                    width: '8px', 
                    height: '8px', 
                    borderRadius: '50%', 
                    backgroundColor: currentRunStatus === 1 ? '#52c41a' : '#d9d9d9',
                    marginRight: '8px',
                  }} />
                  <span style={{ fontSize: '16px', fontWeight: '500' }}>{item.name}</span>
                </div>

                {/* 驱动状态标识 */}
                <div style={{ marginBottom: '10px' }}>
                  <Tag 
                    color={currentRunStatus === 1 ? 'success' : undefined}
                    style={{ 
                      borderRadius: '12px',
                      padding: '4px 12px',
                      fontSize: '12px',
                      border: 'none',
                      backgroundColor: currentRunStatus === 1 ? '#52c41a' : '#8c8c8c',
                      color: 'white',
                    }}
                  >
                    {currentRunStatus === 1 ? '运行中' : '停止'}
                  </Tag>
                </div>

                {/* 当前负载 */}
                <div style={{ marginBottom: '10px' }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    marginBottom: '6px',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <span style={{ fontSize: '14px', color: '#666' }}>当前负载</span>
                      <Tooltip 
                        title="当前负载表示容器运行的整体压力情况。综合 CPU、内存、磁盘和网络使用情况计算得出。数值越大，表示越接近或超过资源上限。"
                        placement="topRight"
                      >
                        <QuestionCircleOutlined style={{ color: '#999', cursor: 'help' }} />
                      </Tooltip>
                    </div>
                    <span style={{ fontSize: '22px', fontWeight: 'bold' }}>
                      {loadPercentage.toFixed(1)}
                    </span>
                  </div>
                  <Progress 
                    percent={loadPercentage} 
                    strokeColor={loadStatus.strokeColor}
                    showInfo={false}
                    size="small"
                  />
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'flex-start', 
                    marginTop: '2px',
                    fontSize: '12px',
                    color: '#999',
                  }}>
                    <span>{latestLoadValue.toFixed(2)}/1.00 负载</span>
                  </div>
                </div>

                {/* 负载状态指示器 - 固定描述 */}
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '12px', 
                  marginBottom: '10px',
                  fontSize: '12px',
                  color: '#666',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <div style={{ 
                      width: '8px', 
                      height: '8px', 
                      borderRadius: '50%', 
                      backgroundColor: '#52c41a',
                    }} />
                    <span>正常 &lt; 0.7</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <div style={{ 
                      width: '8px', 
                      height: '8px', 
                      borderRadius: '50%', 
                      backgroundColor: '#faad14',
                    }} />
                    <span>偏高 0.7~1.0</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <div style={{ 
                      width: '8px', 
                      height: '8px', 
                      borderRadius: '50%', 
                      backgroundColor: '#ff4d4f',
                    }} />
                    <span>过载 ≥ 1.0</span>
                  </div>
                </div>

                {/* 负载趋势图 */}
                <div style={{ 
                  borderRadius: '4px',
                  overflow: 'hidden',
                }}>
                  <MiniAreaChart 
                    data={loadHistoryData[item.id] || []}
                    color={loadStatus.strokeColor} 
                    height={100} 
                  />
                </div>
              </Card>
            </Col>
          );
        })}
      </Row>
    </Card>
  );
}

function SingleLine({ title, tag, per, deviceContainerName }: { title: string; tag?: string; per: string; deviceContainerName: string }) {
  const [metric_tags] = useState<string>(tag || 'container_load');

  const [metric_time] = useState<string>('2min');
  const [list, setList] = useState<{ name: string; time: string; value: number }[]>([]);
  const { run } = useRequest(getServices, {
    defaultParams: [{ metric_time, metric_tags, container_name: deviceContainerName }],
    manual: true, // 设置为手动触发
    pollingInterval: 1000, // 每秒轮询一次
    ready: !!deviceContainerName,
    onSuccess(data) {
      const tList = data.result.series.reduce<{ name: string; time: string; value: number }[]>((pre, cur) => {
        cur.times.forEach((time, index) => {
          pre.push({
            name: cur.container_name,
            time,
            value: cur.values[index],
          });
        });
        return pre;
      }, []);
      setList(tList);
    },
  });

  useEffect(() => {
    if (deviceContainerName) {
      run({ metric_time, metric_tags, container_name: deviceContainerName });
    }
  }, [deviceContainerName, metric_time, run]);
  return (
    <Card className="" title={<>{title}</>}>
      <div className="" style={{ width: '100%', height: '300px', paddingTop: '20px' }}>
        <Line
          data={list}
          xField="time"
          yField="value"
          seriesField="name"
          legend={false}
          yAxis={{ title: { text: `${title}(${per})` }, label: {} }}
          lineStyle={{ lineWidth: 1.5 }}
          autoFit
          smooth
          tooltip={{ formatter: (data) => ({ name: title, value: `${data.value}${per}` }) }}
        ></Line>
      </div>
    </Card>
  );
}

function UpdownColumn({ title, deviceId }: { title: string; deviceId: string }) {
  const [upList, setUpList] = useState<{ time: string; value: number }[]>([]);
  const [downList, setDownList] = useState<{ time: string; value: number }[]>([]);
  const { run } = useRequest(getDeviceMessage, {
    manual: true, // 设置为手动触发
    pollingInterval: 1000, // 每秒轮询一次
    ready: !!deviceId, // 只有当 deviceId 存在时才开始轮询
    onSuccess(data) {
      const _upList: any = [];
      const _downList: any = [];
      data.result.times.forEach((time: string, index: number) => {
        _upList.push({
          time,
          value: data.result.up_value[index],
        });

        _downList.push({
          time,
          value: data.result.dp_value[index],
        });
      });

      setUpList(_upList);
      setDownList(_downList);
    },
  });

  useEffect(() => {
    if (deviceId) {
      run({ id: deviceId });
    }
  }, [deviceId, run]);

  return (
    <Card className="" title={title} style={{ paddingBottom: '20px' }}>
      <div>
        消息上行 {upList[upList.length - 1]?.value} 条/秒
        <div className="" style={{ width: '100%', height: '60px', paddingTop: '20px', paddingBottom: '20px' }}>
          <Column
            data={upList}
            xField="time"
            yField="value"
            xAxis={false}
            yAxis={false}
            color="#1677ff"
            animation={false}
            tooltip={{ formatter: (data) => ({ name: '消息上行', value: `${data.value}  条/秒` }) }}
            columnBackground={{
              style: {
                fill: '#1677ff',
                fillOpacity: 0.1,
              },
            }}
          />
        </div>
      </div>
      <div style={{ marginTop: '40px' }}>
        消息下行 {downList[downList.length - 1]?.value} 条/秒
        <div className="" style={{ width: '100%', height: '60px', paddingTop: '20px', paddingBottom: '20px' }}>
          <Column
            data={downList}
            xField="time"
            yField="value"
            xAxis={false}
            yAxis={false}
            color="#722ed1"
            animation={false}
            tooltip={{ formatter: (data) => ({ name: '消息下行', value: `${data.value}  条/秒` }) }}
            columnBackground={{
              style: {
                fill: '#722ed1',
                fillOpacity: 0.1,
              },
            }}
          />
        </div>
      </div>
    </Card>
  );
}

export default function Drive() {
  const [deviceId, setDeviceId] = useState('');
  const [deviceContainerName, setDeviceContainerName] = useState('');

  return (
    <>
      <NormalBreadcrumb></NormalBreadcrumb>
      <FirstCard deviceId={deviceId} setDeviceId={setDeviceId} setDeviceContainerName={setDeviceContainerName}></FirstCard>
      <div className="m20">
        <Row gutter={[20, 20]}>
          <Col span={24}>
            <UpdownColumn title="消息趋势" deviceId={deviceId}></UpdownColumn>
          </Col>
        </Row>
      </div>
    </>
  );
}
